import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { unwrapResult } from "@reduxjs/toolkit";
import { useDispatch } from "react-redux";
import { loadStripe } from "@stripe/stripe-js";
import {
  createCustomerStripeSession,
  getMoveService,
  getUserByPhoneNumber,
  updateMoveService,
} from "../../redux/action/move-service";
import Header from "../header/Header";
import {
  addInventoryToOpportunity,
  confirmJob,
  createCustomer,
  createJob,
  createLogNotesInOpportunity,
  createOpportunity,
  createRooms,
  getBranches,
  getMoveSizes,
  getReferralSources,
  getTariffs,
  saveSmartMovingResponse,
} from "../../redux/action/smart-move-api";
import StepProgress from "../stepsProgress/stepsProgress";
const strikeKey = process.env.REACT_APP_STRIPE_KEY || "";
type SmartMovingIds = {
  customerId: string;
  opportunityId: string;
  rooms: any[]; // Replace 'any' with proper room type if available
  noteId: string;
  jobId: string;
};
const MoveSummary = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loadingMessage, setLoadingMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [moveData, setMoveData] = useState<any | null>(null);
  const [carrier, setCarrier] = useState<any>(null);
  const { canonicalId, businessName, carrierCanonicalId } = useParams();
  const [commission, setCommission] = useState(0.0);
  const lastStep = 11;
  const [inventory, setInventory] = useState<any>(null);
  const phoneNumber = localStorage.getItem("userPhoneNumber");
  const [user, setUser] = useState<any>(null);
  const [smartMovingOpportunityId, setSmartMovingOpportunityId] = useState("");
  const [smartMovingCustomerId, setSmartMovingCustomerId] = useState("");
  const [currentJobId, setCurrentJobId] = useState("");

  const [noteId, setNoteId] = useState<any>("");
  const [smartMovingRooms, setSmartMovingRooms] = useState<any[]>([]);

  const calculateInitialDeposit = (carrier: any, move: any) => {
    if (carrier.rate.deposit_amount) {
      setCommission(Number(carrier.rate.deposit_amount));
    } else if (carrier.rate.deposit_percentage) {
      const percentageValue = carrier.rate.deposit_percentage / 100;
      setCommission(Math.ceil(move.price_of_items_only * percentageValue));
    }
  };
  useEffect(() => {
    if (canonicalId) {
      const moveServiceData = {
        canonical_id: canonicalId,
        last_step: lastStep,
      };
      try {
        dispatch<any>(getMoveService(canonicalId))
          .then(unwrapResult)
          .then((res: any) => {
            setInventory(res.inventory?.inventory);
            setMoveData(res);
            setCarrier(res.carrier);
            calculateInitialDeposit(res.carrier, res);
            const updateMove = dispatch<any>(
              updateMoveService(moveServiceData)
            );
            unwrapResult(updateMove);
          })
          .catch((err: any) => {
            console.error("Failed to fetch move data:", err);
          });
      } catch (error) {
        console.log(error);
      }
    }
  }, [canonicalId, dispatch]);
  const [bulkyData, setBulkyData] = useState<any>(null);

  useEffect(() => {
    if (inventory) {
      try {
        const inventoryData = JSON.parse(inventory);
        const bulkyItems: any[] = [];

        inventoryData.forEach((room: any) => {
          room.images.forEach((image: any) => {
            image.items.forEach((item: any) => {
              if (item.bulky) {
                bulkyItems.push({
                  object_name: item.object_name,
                  price: item.price,
                });
              }
            });
          });
        });

        setBulkyData(bulkyItems.length > 0 ? bulkyItems : null);
      } catch (error) {
        console.error("Error parsing inventory:", error);
        setBulkyData(null);
      }
    }
  }, [inventory]); // Add inventory as dependency

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const parseItems = (itemsString: string) => {
    try {
      return JSON.parse(itemsString);
    } catch (e) {
      return [];
    }
  };
  const showBulkyItemList = (itemList: string) => {
    const items = JSON.parse(itemList);
    const array = Object.keys(items).map((key) => ({
      name: key,
      price: items[key],
    }));
    return array;
  };
  useEffect(() => {
    if (phoneNumber) {
      dispatch<any>(getUserByPhoneNumber(phoneNumber))
        .then(unwrapResult)
        .then((res: any) => {
          // const currentUserId = res.id
          console.log(res, "-userrr");
          setUser(res);
        })
        .catch((error: any) => {
          console.error("Error fetching user by phone number:", error);
        });
    }
  }, []);
  const formatInventoryForNotes = (inventory: any): string => {
    try {
      let formattedText = "";

      // Parse the inventory if it's a string
      const inventoryData =
        typeof inventory === "string" ? JSON.parse(inventory) : inventory;

      // Check if inventoryData is an array before using forEach
      if (!Array.isArray(inventoryData)) {
        console.error("Inventory is not a valid array:", inventoryData);
        return "No inventory data available";
      }

      inventoryData?.forEach((room: any, roomIndex: number) => {
        formattedText += `Room ${roomIndex + 1}: ${
          room.roomName || "Unnamed Room"
        }\n`;

        room?.images?.forEach((image: any, imageIndex: number) => {
          formattedText += `Image ${imageIndex + 1}:\n`;
          formattedText += `URL: ${image.imageUrl || "No URL available"}\n`;

          image?.items?.forEach((item: any, itemIndex: number) => {
            // Replace "x" with "-" for the count
            formattedText += `${itemIndex + 1}. ${item.count || 1}- ${
              item.object_name || "Unknown Item"
            }\n`;

            if (
              item.predicted_dimensions &&
              Array.isArray(item.predicted_dimensions)
            ) {
              formattedText += `   Dimensions: ${item.predicted_dimensions.join(
                " x "
              )} ft\n`;
            }

            if (item.bulky !== undefined) {
              formattedText += `   Bulky: ${item.bulky ? "Yes" : "No"}\n`;
            }

            if (item.price !== null && item.price !== undefined) {
              formattedText += `   Price: $${item.price}\n`;
            }

            formattedText += "\n";
          });
        });

        formattedText += "\n";
      });

      return formattedText;
    } catch (error) {
      console.error("Error formatting inventory:", error);
      return "Error formatting inventory data";
    }
  };
  useEffect(() => {
    const triggerSmartMovingApis = async () => {
      let customerId: string = "";
      let opportunityId: string = "";
      let rooms: any[] = [];
      let noteId: string = "";
      let jobId: string = "";
      if (carrier?.smart_moving_key && user) {
        try {
          const customerData = {
            carrierId: carrier?.id,
            createCustomer: {
              name: `${user?.first_name} ${user?.last_name}`,
              phoneNumber,
              phoneType: 0,
              emailAddress: user?.email,
              address: "",
              secondaryPhoneNumbers: [],
            },
          };

          setIsLoading(true);
          setLoadingMessage("Fetching configuration...");

          const [referralSourcesRes, branchesRes, tariffsRes, moveSizesRes] =
            await Promise.all([
              dispatch<any>(getReferralSources(carrier.id)).then(unwrapResult),
              dispatch<any>(getBranches(carrier.id)).then(unwrapResult),
              dispatch<any>(getTariffs(carrier.id)).then(unwrapResult),
              dispatch<any>(getMoveSizes(carrier.id)).then(unwrapResult),
            ]);

          const referralSourceId = referralSourcesRes.pageResults[2]?.id;
          const branchId = branchesRes.pageResults[0]?.id;
          const tariffId = tariffsRes.pageResults[0]?.id;
          const moveSizeId = moveSizesRes.pageResults[0]?.id;

          if (!referralSourceId || !branchId || !tariffId || !moveSizeId) {
            throw new Error("Failed to fetch required configuration data");
          }

          setLoadingMessage("Creating customer...");
          dispatch<any>(createCustomer(customerData))
            .then(unwrapResult)
            .then((res: any) => {
              setLoadingMessage("Creating opportunity...");
              customerId = res.id || res;
              setSmartMovingCustomerId(customerId);

              const from_parts = moveData.moving_from.split(",");
              const from_street = from_parts[0]?.trim() || "";
              const from_city = from_parts[1]?.trim() || "";
              const from_state = from_parts[2]?.trim() || "";
              const from_zip = from_parts[3]?.trim() || "";
              const from_country = from_parts[4]?.trim() || "";

              const to_parts = moveData.moving_to.split(",");
              const to_street = to_parts[0]?.trim() || "";
              const to_city = to_parts[1]?.trim() || "";
              const to_state = to_parts[2]?.trim() || "";
              const to_zip = to_parts[3]?.trim() || "";
              const to_country = to_parts[4]?.trim() || "";

              const opportunityPayload = {
                carrierId: carrier?.id,
                createOpportunity: {
                  customerId,
                  referralSourceId,
                  tariffId,
                  branchId,
                  moveDate: moveData.moving_date.split("T")[0],
                  moveSizeId,
                  salesPersonId: "a42c328c-740f-420c-26fb-08dba0017b09",
                  serviceTypeId: 1,
                  utmInformation: {},
                  originAddress: {
                    fullAddress: moveData?.moving_from,
                    street: from_street,
                    unit: "",
                    city: from_city,
                    state: from_state,
                    zip: from_zip,
                    lat: "",
                    lng: "",
                    country: from_country,
                  },
                  destinationAddress: {
                    fullAddress: moveData?.moving_to,
                    street: to_street,
                    unit: "",
                    city: to_city,
                    state: to_state,
                    zip: to_zip,
                    lat: "",
                    lng: "",
                    country: to_country,
                  },
                },
              };

              dispatch<any>(createOpportunity(opportunityPayload))
                .then(unwrapResult)
                .then(async (res: any) => {
                  opportunityId = res.opportunityId || res;
                  setSmartMovingOpportunityId(opportunityId);
                  console.log("Inventory", inventory);
                  if (inventory && inventory !== "") {
                    setLoadingMessage("Creating rooms...");

                    const roomTypes = [
                      {
                        id: "1224b03c-e462-4e04-8fa1-b0f901473af6",
                        name: "Bedroom",
                      },
                      {
                        id: "bc55c016-eb41-44fe-8f8a-b0f901473af6",
                        name: "Dining Room",
                      },
                      {
                        id: "abe2a918-2dcc-4d08-8efc-b0f901473af6",
                        name: "Kids Room",
                      },
                      {
                        id: "9d575d5a-6355-4327-ad19-b0f901473af6",
                        name: "Kitchen",
                      },
                      {
                        id: "c7e7b2de-8a58-49d0-93db-b0f901473af6",
                        name: "Living Room",
                      },
                      {
                        id: "5dd1aaee-a415-41ae-aade-b0f901473af6",
                        name: "Office",
                      },
                      {
                        id: "f3165fd2-08fd-4890-aa15-b0f901473af6",
                        name: "Other",
                      },
                      {
                        id: "82f6d559-8bb2-443d-868c-b0f901473af6",
                        name: "Outdoor",
                      },
                    ];

                    let roomsArray = [];
                    try {
                      roomsArray = Array.isArray(inventory)
                        ? inventory
                        : JSON.parse(inventory);
                      if (!Array.isArray(roomsArray))
                        throw new Error("Inventory is not an array");
                    } catch (e) {
                      console.error("Failed to parse inventory:", e);
                      return;
                    }

                    const roomTypeMap = new Map();
                    roomTypes.forEach((r) =>
                      roomTypeMap.set(r.name.toLowerCase(), r)
                    );

                    const roomsPayload: any[] = [];
                    const seenRooms = new Set();

                    roomsArray.forEach((inventoryRoom) => {
                      const roomName =
                        inventoryRoom.roomName?.toLowerCase() || "other";
                      const matchedType = roomTypeMap.get(roomName) || {
                        id: "f3165fd2-08fd-4890-aa15-b0f901473af6",
                        name: "Other",
                      };

                      if (!seenRooms.has(roomName)) {
                        seenRooms.add(roomName);
                        roomsPayload.push({
                          name: inventoryRoom.roomName,
                          roomTypeId: matchedType.id,
                        });
                      }
                    });
                    console.log("GOING TO CREATE ROOMS ========");
                    try {
                      const createdRooms = await dispatch<any>(
                        createRooms({
                          carrierId: carrier.id,
                          opportunityId,
                          rooms: roomsPayload,
                        })
                      ).then(unwrapResult);

                      rooms = createdRooms;
                      console.log(
                        "GOING TO CREATE ROOMS ========",
                        createdRooms
                      );

                      setSmartMovingRooms(rooms);

                      // ✅ Add inventory to each room
                      setLoadingMessage("Adding inventory to rooms...");
                      for (const createdRoom of createdRooms) {
                        const match = roomsArray.find(
                          (r: any) =>
                            r.roomName.toLowerCase() ===
                            createdRoom.name.toLowerCase()
                        );
                        console.log(
                          "GOING TO CREATE ROOMS matching???? ========",
                          match
                        );

                        if (match?.images?.length) {
                          const items: any[] = [];
                          match.images.forEach((img: any) => {
                            if (img.items) items.push(...img.items);
                          });

                          if (items.length > 0) {
                            await dispatch<any>(
                              addInventoryToOpportunity({
                                carrierId: carrier.id,
                                opportunityId,
                                roomId: createdRoom.id,
                                items,
                              })
                            ).then(unwrapResult);
                          }
                        }
                      }
                    } catch (error) {
                      console.error(
                        "Error creating rooms or adding inventory:",
                        error
                      );
                    }

                    // Create Notes
                    const formattedInventory =
                      formatInventoryForNotes(inventory);
                    const notesData = {
                      carrierId: carrier.id,
                      opportunityId,
                      notes: formattedInventory,
                    };
                    dispatch<any>(createLogNotesInOpportunity(notesData))
                      .then(unwrapResult)
                      .then((res: any) => {
                        noteId = res.noteId;
                        setNoteId(noteId);
                      });
                  }

                  // Create Job
                  setLoadingMessage("Creating job...");
                  const jobData = {
                    carrierId: carrier?.id,
                    createService: { serviceType: 1 },
                  };

                  dispatch<any>(createJob({ opportunityId, jobData }))
                    .then(unwrapResult)
                    .then((res: any) => {
                      jobId = res.jobId || res;
                      setCurrentJobId(jobId);
                    });

                  setIsLoading(false);
                });
            })
            .catch((error: any) => {
              console.error("Error in SmartMoving flow:", error);
              return false;
            });
        } catch (error) {
          console.error("Error in SmartMoving APIs:", error);
          return false;
        }
      }
    };

    triggerSmartMovingApis();
  }, [carrier]);
  const [hasSaved, setHasSaved] = useState(false);

  useEffect(() => {
    const saveData = async () => {
      if (
        !hasSaved &&
        smartMovingCustomerId &&
        smartMovingOpportunityId &&
        currentJobId
      ) {
        try {
          setLoadingMessage("Saving response...");
          setIsLoading(true);

          const smartMovingData = {
            customer_id: smartMovingCustomerId,
            opportunity_id: smartMovingOpportunityId,
            job_id: currentJobId,
            rooms: smartMovingRooms,
            notes_id: noteId,
            userId: user?.id,
          };

          dispatch<any>(saveSmartMovingResponse(smartMovingData))
            .then(unwrapResult)
            .then((res: any) => {
              console.log(res, "Data saved successfully");
            });
          setHasSaved(true);
        } catch (error) {
          return false;
        } finally {
          setIsLoading(false);
        }
      }
    };

    saveData();
  }, [smartMovingCustomerId, smartMovingOpportunityId, currentJobId, hasSaved]);
  const smartMoving = async () => {
    if (carrier?.smart_moving_key) {
      try {
        // Create Job

        setLoadingMessage("Confirming job...");
        const confirmData = {
          opportunityId: smartMovingOpportunityId,
          currentJobId,
          carrierId: Number(carrier?.id), // Ensure this is a number
        };

        dispatch<any>(confirmJob(confirmData))
          .then(unwrapResult)
          .then((res: any) => {
            console.log(res);
          });
        return true;
      } catch (error) {
        return false;
      }
    }
    return false;
  };
  const createCheckoutSession = async () => {
    setIsLoading(true);
    setLoadingMessage("Creating Checkout Session...");
    await smartMoving();
    const stripe = await loadStripe(strikeKey);
    const checkoutParams = {
      price: commission * 100,
      carrierId: carrier.id,
      canonical_id: canonicalId,
    };
    dispatch<any>(createCustomerStripeSession(checkoutParams))
      .then(unwrapResult)
      .then((responseSession: any) => {
        stripe?.redirectToCheckout({
          sessionId: responseSession.sessionId,
        });
        setTimeout(() => {
          setIsLoading(false);
        }, 1300);
      })
      .catch((err: any) => {
        console.log(err);
        setTimeout(() => {
          setIsLoading(false);
        }, 1300);
      });
  };
  return (
    <div>
      <Header />
      <StepProgress currentStep={7} />
      <Container
        sx={{
          width: "100%",
          maxWidth: "100%",
          minHeight: isMobile ? "calc(100vh - 64px)" : "80vh",
          boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
          padding: isMobile ? "16px 12px" : "20px",
          background: "#f2f0f0", // Background color outside the main card
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            paddingBottom: isMobile ? "80px" : "20px",
            // White container box matching the image
            backgroundColor: "#fff",
            borderRadius: "8px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
            p: isMobile ? 2 : 3,
          }}
        >
          <Typography
            fontWeight={700}
            fontSize={isMobile ? "18px" : "20px"}
            textAlign="center"
            mb={1}
          >
            Move Summary
          </Typography>
          <Typography
            textAlign="center"
            fontSize={isMobile ? 14 : 16}
            mb={isMobile ? 2 : 3}
          >
            Review your booking details before confirmation
          </Typography>

          {moveData && (
            <Box
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                padding: isMobile ? "12px" : "16px",
                mb: isMobile ? 2 : 3,
                background: "#f9f9f9",
              }}
            >
              {/* Move Number Row (Header style) */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexDirection: "row",
                  mb: isMobile ? 1.5 : 2,
                  gap: isMobile ? 0.5 : 0,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography component="span" sx={{ fontSize: 18, mr: 1 }}>
                    {" "}
                    🔢{" "}
                  </Typography>
                  <Typography fontWeight={600} fontSize={isMobile ? 14 : 16}>
                    Move Number:
                  </Typography>
                </Box>
                <Typography
                  fontWeight={400}
                  fontSize={isMobile ? 14 : 16}
                  textAlign="right"
                  sx={{
                    wordBreak: "break-word",
                    color: theme.palette.primary.main,
                    fontWeight: 700,
                  }}
                >
                  {moveData?.move_order_number || "Not provided"}{" "}
                </Typography>
              </Box>
              <Divider sx={{ background: "#D3D3D3", mb: isMobile ? 1.5 : 2 }} />

              {/* Name Row */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: isMobile ? "flex-start" : "center",
                  flexDirection: isMobile ? "column" : "row",
                  mb: isMobile ? 1.5 : 2,
                  gap: isMobile ? 0.5 : 0,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography component="span" sx={{ fontSize: 18, mr: 1 }}>
                    {" "}
                    👤{" "}
                  </Typography>
                  <Typography fontWeight={600} fontSize={isMobile ? 14 : 16}>
                    Name:
                  </Typography>
                </Box>
                <Typography
                  fontWeight={400}
                  fontSize={isMobile ? 14 : 16}
                  textAlign={isMobile ? "left" : "right"}
                  sx={{ wordBreak: "break-word" }}
                >
                  {moveData?.user?.first_name || "Not provided"}{" "}
                  {moveData?.user?.last_name || ""}
                </Typography>
              </Box>
              <Divider sx={{ background: "#D3D3D3", mb: isMobile ? 1.5 : 2 }} />

              {/* Email Row (Inferred from image) */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: isMobile ? "flex-start" : "center",
                  flexDirection: isMobile ? "column" : "row",
                  mb: isMobile ? 1.5 : 2,
                  gap: isMobile ? 0.5 : 0,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography component="span" sx={{ fontSize: 18, mr: 1 }}>
                    {" "}
                    ✉️{" "}
                  </Typography>
                  <Typography fontWeight={600} fontSize={isMobile ? 14 : 16}>
                    Email:
                  </Typography>
                </Box>
                <Typography
                  fontWeight={400}
                  fontSize={isMobile ? 14 : 16}
                  textAlign={isMobile ? "left" : "right"}
                  sx={{ wordBreak: "break-word" }}
                >
                  {moveData?.user?.email || "Not provided"}
                </Typography>
              </Box>
              <Divider sx={{ background: "#D3D3D3", mb: isMobile ? 1.5 : 2 }} />

              {/* Phone Number Row */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: isMobile ? "flex-start" : "center",
                  flexDirection: isMobile ? "column" : "row",
                  mb: isMobile ? 1.5 : 2,
                  gap: isMobile ? 0.5 : 0,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography component="span" sx={{ fontSize: 18, mr: 1 }}>
                    {" "}
                    📞{" "}
                  </Typography>
                  <Typography fontWeight={600} fontSize={isMobile ? 14 : 16}>
                    Phone Number:
                  </Typography>
                </Box>
                <Typography
                  fontWeight={400}
                  fontSize={isMobile ? 14 : 16}
                  textAlign={isMobile ? "left" : "right"}
                  sx={{ wordBreak: "break-word" }}
                >
                  {moveData?.user?.phone_number
                    ? "+1" + moveData?.user?.phone_number
                    : "Not provided"}
                </Typography>
              </Box>
              <Divider sx={{ background: "#D3D3D3", mb: isMobile ? 1.5 : 2 }} />

              {/* Service Type Row */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: isMobile ? "flex-start" : "center",
                  flexDirection: isMobile ? "column" : "row",
                  mb: isMobile ? 1.5 : 2,
                  gap: isMobile ? 0.5 : 0,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography component="span" sx={{ fontSize: 18, mr: 1 }}>
                    {" "}
                    🚚{" "}
                  </Typography>
                  <Typography fontWeight={600} fontSize={isMobile ? 14 : 16}>
                    Service Type:
                  </Typography>
                </Box>
                <Typography
                  fontWeight={400}
                  fontSize={isMobile ? 14 : 16}
                  textAlign={isMobile ? "left" : "right"}
                >
                  {Number(moveData?.distance) > 200
                    ? "Long Distance"
                    : "Moving Services"}
                </Typography>
              </Box>
              <Divider sx={{ background: "#D3D3D3", mb: isMobile ? 1.5 : 2 }} />

              {/* Number of Bedrooms Row */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: isMobile ? "flex-start" : "center",
                  flexDirection: isMobile ? "column" : "row",
                  mb: isMobile ? 1.5 : 2,
                  gap: isMobile ? 0.5 : 0,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography component="span" sx={{ fontSize: 18, mr: 1 }}>
                    {" "}
                    🏠{" "}
                  </Typography>
                  <Typography fontWeight={600} fontSize={isMobile ? 14 : 16}>
                    Number of Bedrooms:
                  </Typography>
                </Box>
                <Typography
                  fontWeight={400}
                  fontSize={isMobile ? 14 : 16}
                  textAlign={isMobile ? "left" : "right"}
                >
                  {moveData?.number_of_bedrooms}
                </Typography>
              </Box>
              <Divider sx={{ background: "#D3D3D3", mb: isMobile ? 1.5 : 2 }} />

              {/* Volume Details Row (Matching image) */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: isMobile ? "flex-start" : "center",
                  flexDirection: isMobile ? "column" : "row",
                  mb: isMobile ? 1.5 : 2,
                  gap: isMobile ? 0.5 : 0,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography component="span" sx={{ fontSize: 18, mr: 1 }}>
                    {" "}
                    📦{" "}
                  </Typography>
                  <Typography fontWeight={600} fontSize={isMobile ? 14 : 16}>
                    Volume Details:
                  </Typography>
                </Box>
                <Typography
                  fontWeight={400}
                  fontSize={isMobile ? 14 : 16}
                  textAlign={isMobile ? "left" : "right"}
                >
                  Average (Typical amount)
                </Typography>
              </Box>
              <Divider sx={{ background: "#D3D3D3", mb: isMobile ? 1.5 : 2 }} />

              {/* Date of Move Row */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: isMobile ? "flex-start" : "center",
                  flexDirection: isMobile ? "column" : "row",
                  mb: isMobile ? 1.5 : 2,
                  gap: isMobile ? 0.5 : 0,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography component="span" sx={{ fontSize: 18, mr: 1 }}>
                    {" "}
                    🗓️{" "}
                  </Typography>
                  <Typography fontWeight={600} fontSize={isMobile ? 14 : 16}>
                    Date of Move:
                  </Typography>
                </Box>
                <Typography
                  fontWeight={400}
                  fontSize={isMobile ? 14 : 16}
                  textAlign={isMobile ? "left" : "right"}
                >
                  {formatDate(moveData?.moving_date)}
                </Typography>
              </Box>
              <Divider sx={{ background: "#D3D3D3", mb: isMobile ? 1.5 : 2 }} />

              {/* Pickup Location Row */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: isMobile ? "flex-start" : "center",
                  flexDirection: isMobile ? "column" : "row",
                  mb: isMobile ? 1.5 : 2,
                  gap: isMobile ? 0.5 : 0,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography component="span" sx={{ fontSize: 18, mr: 1 }}>
                    {" "}
                    📍{" "}
                  </Typography>
                  <Typography fontWeight={600} fontSize={isMobile ? 14 : 16}>
                    Pickup Location:
                  </Typography>
                </Box>
                <Typography
                  fontWeight={400}
                  fontSize={isMobile ? 14 : 16}
                  sx={{
                    wordBreak: "break-word",
                    textAlign: isMobile ? "left" : "right",
                  }}
                >
                  {moveData?.moving_from}
                </Typography>
              </Box>
              <Divider sx={{ background: "#D3D3D3", mb: isMobile ? 1.5 : 2 }} />

              {/* Delivery Location Row */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: isMobile ? "flex-start" : "center",
                  flexDirection: isMobile ? "column" : "row",
                  mb: isMobile ? 1.5 : 2,
                  gap: isMobile ? 0.5 : 0,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography component="span" sx={{ fontSize: 18, mr: 1 }}>
                    {" "}
                    🏁{" "}
                  </Typography>
                  <Typography fontWeight={600} fontSize={isMobile ? 14 : 16}>
                    Delivery Location:
                  </Typography>
                </Box>
                <Typography
                  fontWeight={400}
                  fontSize={isMobile ? 14 : 16}
                  sx={{
                    wordBreak: "break-word",
                    textAlign: isMobile ? "left" : "right",
                  }}
                >
                  {moveData?.moving_to}
                </Typography>
              </Box>
              <Divider sx={{ background: "#D3D3D3", mb: isMobile ? 1.5 : 2 }} />

              {/* Dwelling Type Row (Inferred from image) */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: isMobile ? "flex-start" : "center",
                  flexDirection: isMobile ? "column" : "row",
                  mb: isMobile ? 1.5 : 2,
                  gap: isMobile ? 0.5 : 0,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography component="span" sx={{ fontSize: 18, mr: 1 }}>
                    {" "}
                    🏡{" "}
                  </Typography>
                  <Typography fontWeight={600} fontSize={isMobile ? 14 : 16}>
                    Dwelling Type:
                  </Typography>
                </Box>
                <Typography
                  fontWeight={400}
                  fontSize={isMobile ? 14 : 16}
                  textAlign={isMobile ? "left" : "right"}
                >
                  {moveData?.dwelling_type || "Home"}
                </Typography>
              </Box>
              <Divider sx={{ background: "#D3D3D3", mb: isMobile ? 1.5 : 2 }} />

              {/* Bulky Items Section (Matches image style) */}
              <Box sx={{ mb: 0 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: isMobile ? "flex-start" : "center",
                    justifyContent: "space-between",
                    flexDirection: "row",
                    padding: "8px 0",
                    gap: isMobile ? 0.5 : 0,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography component="span" sx={{ fontSize: 18, mr: 1 }}>
                      {" "}
                      💪{" "}
                    </Typography>
                    <Typography fontWeight={600} fontSize={isMobile ? 14 : 16}>
                      Bulky Items:
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ pl: isMobile ? 3 : 3 }}>
                  {Number(moveData?.distance) > 200 ? (
                    bulkyData ? (
                      bulkyData.map((item: any, index: any) => (
                        <Typography
                          key={index}
                          fontSize={isMobile ? 13 : 14}
                          sx={{ mb: 0.5, wordBreak: "break-word" }}
                        >
                          {item.object_name} - ${item.price}
                        </Typography>
                      ))
                    ) : (
                      <Typography fontSize={isMobile ? 13 : 14}>
                        No Bulky Items
                      </Typography>
                    )
                  ) : moveData.items ? (
                    JSON.parse(moveData?.items).map((item: any, index: any) => (
                      <Typography
                        key={index}
                        fontSize={isMobile ? 13 : 14}
                        sx={{ mb: 0.5, wordBreak: "break-word" }}
                      >
                        {item.name} - ${item.fee}
                      </Typography>
                    ))
                  ) : (
                    <Typography fontSize={isMobile ? 13 : 14}>
                      No Bulky Items
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}

          {/* PRICE BOXES SECTION */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: isMobile ? 1.5 : 2,
              mb: isMobile ? 2 : 3,
              flexDirection: isMobile ? "column" : "row",
            }}
          >
            {/* Total Move Cost Box */}
            <Box
              sx={{
                flex: 1,
                backgroundColor: "#fff",
                border: "1px solid #D3D3D3",
                borderRadius: "12px",
                padding: isMobile ? "12px" : "16px",
              }}
            >
              <Typography
                fontWeight={700}
                fontSize={isMobile ? 14 : 16}
                color="#000"
                mb={0.5}
              >
                Total Move Cost
              </Typography>
              <Typography
                fontWeight={700}
                fontSize={isMobile ? 24 : 32}
                color="#000"
              >
                {moveData?.packingPrice
                  ? `$${Math.ceil(
                      moveData?.packingPrice + Number(moveData?.price)
                    )}`
                  : `$${Math.ceil(moveData?.price)}`}
              </Typography>
              <Typography fontSize={isMobile ? 12 : 14} color="#666">
                Including all services and fees
              </Typography>
            </Box>

            {/* Cost to Book Move Box */}
            <Box
              sx={{
                flex: 1,
                backgroundColor: "#E6F6E6", // Light Green background
                border: "1px solid #78C378",
                borderRadius: "12px",
                padding: isMobile ? "12px" : "16px",
              }}
            >
              <Typography
                fontWeight={700}
                fontSize={isMobile ? 14 : 16}
                color="#388E3C" // Success green color
                mb={0.5}
              >
                Cost to Book Move
              </Typography>
              <Typography
                fontWeight={700}
                fontSize={isMobile ? 24 : 32}
                color="#388E3C"
              >
                {`$${commission}`}
              </Typography>
              <Typography fontSize={isMobile ? 12 : 14} color="#388E3C">
                Pay now to secure your booking
              </Typography>
            </Box>
          </Box>

          {/* Secure Booking Guarantee Box (Matches image style) */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              p: isMobile ? 1.5 : 2,
              mb: isMobile ? 3 : 4,
              borderRadius: "12px",
              backgroundColor: "#F0F8FF", // Light blue background
              border: "1px solid #B0C4DE",
            }}
          >
            <Typography component="span" sx={{ fontSize: 24, mr: 1, mt: -0.5 }}>
              🛡️
            </Typography>
            <Box>
              <Typography
                fontWeight={600}
                fontSize={isMobile ? 14 : 16}
                color={theme.palette.primary.main}
              >
                Secure Booking Guarantee
              </Typography>
              <Typography fontSize={isMobile ? 12 : 14} color="#444">
                You will be charged ${commission}. The remaining balance of $
                {moveData?.packingPrice
                  ? Math.ceil(
                      Number(moveData?.packingPrice) + Number(moveData.price)
                    ) - Number(commission)
                  : Number(moveData?.price) - Number(commission)}{" "}
                will be paid after your move is completed. Cancel anytime up to
                48 hours before your move date.
              </Typography>
            </Box>
          </Box>

          {/* Loading Backdrop (Functionality is not changed) */}
          {isLoading && (
            <Backdrop
              sx={{
                color: `${theme.palette.primary.main}`,
                zIndex: (theme) => theme.zIndex.drawer + 1,
                backdropFilter: "blur(5px)",
                backgroundColor: "rgba(0,0,0,0.2)",
              }}
              open={isLoading}
            >
              <CircularProgress size={50} color="inherit" />
              <Typography
                variant="h6"
                sx={{
                  mt: 2,
                  color: `${theme.palette.primary.main}`,
                  marginLeft: 1,
                }}
              >
                {loadingMessage}
              </Typography>
            </Backdrop>
          )}
        </Box>

        {/* Footer Button Section */}
        {moveData ? (
          <Box
            sx={{
              width: "100%",
              paddingBottom: isMobile ? "0" : "20px",
              position: isMobile ? "fixed" : "relative",
              bottom: isMobile ? 0 : "auto",
              left: isMobile ? 0 : "auto",
              right: isMobile ? 0 : "auto",
              padding: isMobile ? "12px 16px" : "0", // Padding for mobile bottom bar
              backgroundColor: isMobile ? "#f2f0f0" : "transparent",
              borderTop: isMobile ? "1px solid #e0e0e0" : "none",
              zIndex: isMobile ? 1000 : "auto",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Button
              variant="contained"
              sx={{
                width: isMobile ? "100%" : "100%",
                maxWidth: isMobile ? "none" : "100%",
                borderRadius: "20px",
                backgroundColor: theme.palette.primary.main,
                color: "#fff",
                padding: "12px 24px",
                textTransform: "none",
                fontSize: "14px",
                fontWeight: "600",
                "&:hover": { backgroundColor: "#357ABD" },
                margin: 0,
                boxSizing: "border-box",
                mb: 1.5,
              }}
              onClick={() => {
                if (
                  moveData?.initial_deposit === null &&
                  !moveData.payment_response
                ) {
                  createCheckoutSession();
                } else {
                  navigate(
                    `/${businessName}/${carrierCanonicalId}/consumer-dashboard/${moveData.user.canonical_id}`
                  );
                }
              }}
            >
              {moveData?.initial_deposit === null && !moveData.payment_response
                ? "✓  Confirm Move"
                : "Go To Dashboard"}
            </Button>
            <Typography
              onClick={() => console.log("Back to Edit Details")} // Placeholder for the actual navigation logic
              sx={{
                fontSize: 14,
                fontWeight: 600,
                color: theme.palette.primary.main,
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              Back to Edit Details
            </Typography>
          </Box>
        ) : null}
      </Container>
    </div>
  );
};

export default MoveSummary;
